import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  LinearProgress,
  Grid,
  useMediaQuery,
  useTheme,
  Fab,
  Zoom,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  PlayCircle as PlayIcon,
  CheckCircle as CompletedIcon,
  School as SchoolIcon,
  Timer as TimerIcon,
  Flag as ObjectiveIcon,
  PlayArrow as StartIcon,
  Article as LessonsIcon,
} from '@mui/icons-material';
import { courseApi } from '../services/api';

interface Lesson {
  id: string;
  slug: string;
  title: string;
  contentType: string;
  estimatedDurationMinutes: number | null;
  pointsReward: number;
  sortOrder: number;
}

interface Module {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  sortOrder: number;
  lessons: Lesson[];
  _count: { lessons: number };
}

interface Course {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  pointsReward: number;
  workArea: { name: string } | null;
  skillLevel: { name: string } | null;
  modules: Module[];
  enrollment: { status: string; progressPercent: number } | null;
  objectives?: string[];
  estimatedDurationMinutes?: number;
}

function CourseDetailPage() {
  const { courseId } = useParams<{ courseId: string }>();

  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams, setSearchParams] = useSearchParams();

  const [autoOpenHandled, setAutoOpenHandled] = useState(false);

  // Fetch course details (using ID)
  const { data: course, isLoading, error } = useQuery({
    queryKey: ['course', courseId],
    queryFn: () => courseApi.getCourse(courseId!).then((r) => r.data as Course),
    enabled: !!courseId,
  });

  // Enroll mutation
  const enrollMutation = useMutation({
    mutationFn: () => courseApi.enrollCourse(courseId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['course', courseId] });
    },
  });

  // Helper to check if string is UUID format
  const isUUID = (str: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);

  // Redirect to UUID-based URL if using slug (to ensure consistent URLs)
  useEffect(() => {
    if (course && courseId && !isUUID(courseId)) {
      navigate(`/courses/${course.id}`, { replace: true });
    }
  }, [course, courseId, navigate]);

  // Handle auto-open lesson from deep-link query parameter
  useEffect(() => {
    if (autoOpenHandled || !course || isLoading) return;

    const lessonSlug = searchParams.get('lesson');
    if (!lessonSlug) {
      setAutoOpenHandled(true);
      return;
    }

    // Check enrollment and auto-enroll if needed
    const isEnrolled =
      course.enrollment?.status === 'enrolled' ||
      course.enrollment?.status === 'in_progress' ||
      course.enrollment?.status === 'completed';

    if (!isEnrolled) {
      // Auto-enroll first
      enrollMutation.mutate(undefined, {
        onSuccess: () => {
          // After enrollment, find and open the lesson
          openLessonBySlug(lessonSlug);
        },
      });
    } else {
      // Already enrolled, open the lesson directly
      openLessonBySlug(lessonSlug);
    }

    // Remove the lesson param from URL after handling
    setSearchParams({}, { replace: true });
    setAutoOpenHandled(true);
  }, [course, isLoading, autoOpenHandled, searchParams]);

  const openLessonBySlug = (lessonSlug: string) => {
    if (!course) return;

    // Find the lesson across all modules
    for (const module of course.modules) {
      const lesson = module.lessons.find((l) => l.slug === lessonSlug);
      if (lesson) {
        handlePlayLesson(lesson);
        return;
      }
    }
    console.warn('Lesson not found in course:', lessonSlug);
  };

  const handlePlayLesson = (lesson: Lesson) => {
    // Navigate to the dedicated lesson page using UUIDs
    navigate(`/courses/${course?.id || courseId}/lessons/${lesson.id}`);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !course) {
    return (
      <Box>
        <Alert severity="error">
          {t('errors.notFound')} - Course not found
        </Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/courses')} sx={{ mt: 2 }}>
          {t('common.back')}
        </Button>
      </Box>
    );
  }

  const isEnrolled = course.enrollment?.status === 'enrolled' || course.enrollment?.status === 'in_progress' || course.enrollment?.status === 'completed';

  // Calculate total duration from all lessons
  const totalDurationMinutes = course.modules.reduce((total, module) => {
    return total + module.lessons.reduce((lessonTotal, lesson) => {
      return lessonTotal + (lesson.estimatedDurationMinutes || 0);
    }, 0);
  }, 0);

  // Total lessons count
  const totalLessons = course.modules.reduce((acc, m) => acc + m.lessons.length, 0);

  // Find first lesson for starting/continuing
  const firstLesson = course.modules
    .sort((a, b) => a.sortOrder - b.sortOrder)[0]?.lessons
    .sort((a, b) => a.sortOrder - b.sortOrder)[0];

  const handleStartOrContinue = () => {
    if (firstLesson && course) {
      if (!isEnrolled) {
        enrollMutation.mutate(undefined, {
          onSuccess: () => {
            navigate(`/courses/${course.id}/lessons/${firstLesson.id}`);
          },
        });
      } else {
        navigate(`/courses/${course.id}/lessons/${firstLesson.id}`);
      }
    }
  };

  return (
    <Box sx={{ pb: isMobile ? 10 : 0 }}> {/* Padding for fixed button on mobile */}
      {/* Breadcrumbs - simplified on mobile */}
      {!isMobile && (
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            underline="hover"
            color="inherit"
            onClick={() => navigate('/courses')}
          >
            {t('courses.title')}
          </Link>
          <Typography color="text.primary">{course.title}</Typography>
        </Breadcrumbs>
      )}

      {/* Back button for mobile */}
      {isMobile && (
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/courses')}
          sx={{ mb: 1 }}
          size="small"
        >
          Kurse
        </Button>
      )}

      {/* Course Header */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* Course info - full width on mobile */}
          <Grid item xs={12} md={8}>
            <Typography variant={isMobile ? 'h5' : 'h4'} gutterBottom>
              {course.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {course.description}
            </Typography>

            {/* Meta info chips - horizontal scroll on mobile */}
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: isMobile ? 'nowrap' : 'wrap',
                mb: 2,
                overflowX: isMobile ? 'auto' : 'visible',
                pb: isMobile ? 0.5 : 0,
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
              }}
            >
              {course.workArea && (
                <Chip label={course.workArea.name} variant="outlined" size="small" sx={{ flexShrink: 0 }} />
              )}
              {course.skillLevel && (
                <Chip label={course.skillLevel.name} size="small" sx={{ flexShrink: 0 }} />
              )}
              <Chip
                icon={<LessonsIcon />}
                label={`${totalLessons} Lektionen`}
                variant="outlined"
                size="small"
                sx={{ flexShrink: 0 }}
              />
              {totalDurationMinutes > 0 && (
                <Chip
                  icon={<TimerIcon />}
                  label={totalDurationMinutes >= 60
                    ? `${Math.floor(totalDurationMinutes / 60)}h ${totalDurationMinutes % 60}m`
                    : `${totalDurationMinutes} Min.`}
                  variant="outlined"
                  size="small"
                  sx={{ flexShrink: 0 }}
                />
              )}
              <Chip
                label={`${course.pointsReward} P`}
                color="primary"
                size="small"
                sx={{ flexShrink: 0 }}
              />
            </Box>

            {/* Progress bar for enrolled users */}
            {isEnrolled && course.enrollment?.progressPercent !== undefined && (
              <Box sx={{ mb: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    Fortschritt
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {course.enrollment.progressPercent}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={course.enrollment.progressPercent}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            )}

            {/* Learning objectives - collapsed on mobile */}
            {course.objectives && course.objectives.length > 0 && !isMobile && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ObjectiveIcon fontSize="small" color="primary" />
                  Lernziele
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {course.objectives.map((objective, index) => (
                    <Typography component="li" variant="body2" key={index} sx={{ mb: 0.5 }}>
                      {objective}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
          </Grid>

          {/* Right side - Action button (hidden on mobile, shown as fixed button) */}
          {!isMobile && (
            <Grid item xs={12} md={4}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  textAlign: 'center',
                  bgcolor: 'background.default',
                }}
              >
                {!isEnrolled ? (
                  <>
                    <Typography variant="h6" gutterBottom>
                      Bereit zu starten?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Schreiben Sie sich ein und beginnen Sie mit dem Lernen.
                    </Typography>
                    <Button
                      variant="contained"
                      size="large"
                      fullWidth
                      startIcon={<StartIcon />}
                      onClick={handleStartOrContinue}
                      disabled={enrollMutation.isPending || !firstLesson}
                    >
                      {enrollMutation.isPending ? t('common.loading') : 'Kurs starten'}
                    </Button>
                  </>
                ) : (
                  <>
                    <Chip
                      icon={<SchoolIcon />}
                      label={t('courses.enrolled')}
                      color="success"
                      sx={{ mb: 2 }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {course.enrollment?.progressPercent === 100
                        ? 'Herzlichen Glückwunsch! Sie haben diesen Kurs abgeschlossen.'
                        : 'Setzen Sie Ihren Fortschritt fort.'}
                    </Typography>
                    <Button
                      variant="contained"
                      size="large"
                      fullWidth
                      startIcon={<StartIcon />}
                      onClick={handleStartOrContinue}
                      disabled={!firstLesson}
                    >
                      {course.enrollment?.progressPercent === 0 ? 'Kurs starten' : 'Fortsetzen'}
                    </Button>
                  </>
                )}
              </Paper>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Fixed Start Button for Mobile */}
      {isMobile && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 56, // Above bottom navigation
            left: 0,
            right: 0,
            p: 2,
            zIndex: 100,
            borderRadius: 0,
          }}
        >
          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={<StartIcon />}
            onClick={handleStartOrContinue}
            disabled={enrollMutation.isPending || !firstLesson}
            sx={{ minHeight: 48 }}
          >
            {enrollMutation.isPending
              ? t('common.loading')
              : !isEnrolled
                ? 'Kurs starten'
                : course.enrollment?.progressPercent === 0
                  ? 'Kurs starten'
                  : 'Fortsetzen'}
          </Button>
        </Paper>
      )}

      {/* Modules List */}
      <Typography variant={isMobile ? 'h6' : 'h5'} gutterBottom>
        Module ({course.modules.length})
      </Typography>

      {course.modules
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((module) => {
          const moduleDuration = module.lessons.reduce(
            (sum, l) => sum + (l.estimatedDurationMinutes || 0),
            0
          );
          return (
            <Card key={module.id} sx={{ mb: 2 }}>
              <ListItemButton
                onClick={() => navigate(`/courses/${course.id}/modules/${module.id}`)}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  minHeight: isMobile ? 72 : 'auto', // Touch-friendly height
                }}
              >
                <ListItemIcon sx={{ minWidth: isMobile ? 40 : 56 }}>
                  <SchoolIcon color="primary" fontSize={isMobile ? 'medium' : 'large'} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant={isMobile ? 'subtitle1' : 'h6'} component="span" fontWeight="medium">
                      {module.title}
                    </Typography>
                  }
                  secondary={
                    <Box component="span">
                      {module.description && !isMobile && (
                        <Typography variant="body2" color="text.secondary" component="span" display="block">
                          {module.description}
                        </Typography>
                      )}
                      <Box component="span" display="flex" gap={0.5} mt={0.5}>
                        <Chip
                          size="small"
                          label={`${module.lessons.length} Lekt.`}
                          variant="outlined"
                          sx={{ height: 24 }}
                        />
                        {moduleDuration > 0 && (
                          <Chip
                            size="small"
                            label={`${moduleDuration}m`}
                            variant="outlined"
                            sx={{ height: 24 }}
                          />
                        )}
                      </Box>
                    </Box>
                  }
                />
              </ListItemButton>
            </Card>
          );
        })}

      {!isEnrolled && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Bitte schreiben Sie sich zuerst in diesen Kurs ein, um die Module zu starten.
        </Alert>
      )}
    </Box>
  );
}

export default CourseDetailPage;
